import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Search, Pencil, Trash2, Loader2, UserCog, KeyRound, ShieldCheck, Users, IdCard,
} from "lucide-react";
import { toast } from "sonner";
import ModuleSelector from "@/components/admin/ModuleSelector";
import PasswordModal from "@/components/admin/PasswordModal";
import ResetActionsModal from "@/components/admin/ResetActionsModal";

const ROLE_LABELS = {
  admin: { label: "Pääkäyttäjä", cls: "bg-amber-500/20 text-amber-200 border-amber-500/30" },
  staff: { label: "Henkilökunta", cls: "bg-blue-500/20 text-blue-200 border-blue-500/30" },
  license_customer: { label: "Lisenssiasiakas", cls: "bg-purple-500/20 text-purple-200 border-purple-500/30" },
  user: { label: "Käyttäjä", cls: "bg-zinc-600/30 text-zinc-200 border-zinc-500/30" },
};

function StaffForm({ user, onSaved, onClose, onShowPassword }) {
  const isEdit = !!user;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: user?.email || "",
    password: "",
    full_name: user?.full_name || "",
    role: user?.role || "staff",
    company_name: user?.company_name || "",
    phone: user?.phone || "",
    modules: user?.modules || [],
    notes: user?.notes || "",
    is_active: user?.is_active ?? true,
    can_create_external_invites: user?.can_create_external_invites ?? false,
  });

  const save = async () => {
    if (!form.email && !isEdit) return toast.error("Sähköposti vaaditaan");
    if (!form.full_name) return toast.error("Nimi vaaditaan");
    setLoading(true);
    try {
      if (isEdit) {
        const data = {
          full_name: form.full_name,
          role: form.role,
          modules: form.modules,
          company_name: form.company_name || null,
          phone: form.phone || null,
          notes: form.notes || null,
          is_active: form.is_active,
          can_create_external_invites: !!form.can_create_external_invites,
        };
        await api.patch(`/admin/users/${user.id}`, data);
        toast.success("Käyttäjä päivitetty");
      } else {
        const data = {
          email: form.email.trim().toLowerCase(),
          full_name: form.full_name,
          role: form.role,
          modules: form.modules,
          company_name: form.company_name || null,
          phone: form.phone || null,
          notes: form.notes || null,
          can_create_external_invites: !!form.can_create_external_invites,
        };
        if (form.password) data.password = form.password;
        const res = await api.post("/admin/users", data);
        toast.success("Käyttäjä luotu – aktiivinen heti");
        if (res.data.initial_password) {
          onShowPassword?.({ email: res.data.email, password: res.data.initial_password });
        }
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="staff-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Sähköposti *</Label>
          <Input
            value={form.email}
            disabled={isEdit}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="nimi@yritys.fi"
            className="mt-1"
            data-testid="staff-email"
          />
          {isEdit && <p className="text-[10px] text-muted-foreground mt-1">Sähköpostia ei voi muuttaa muokkauksessa</p>}
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Koko nimi *</Label>
          <Input
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="mt-1"
            data-testid="staff-name"
          />
        </div>
        <div>
          <Label className="text-xs">Yritys</Label>
          <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Puhelin</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Rooli</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="staff">Henkilökunta</SelectItem>
              <SelectItem value="admin">Pääkäyttäjä</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1">
            Lisenssiasiakkaat luodaan "Lisenssit" -näkymästä
          </p>
        </div>
        {!isEdit && (
          <div>
            <Label className="text-xs">Salasana (valinnainen)</Label>
            <Input
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Tyhjä = generoidaan automaattisesti"
              type="text"
              className="mt-1 font-mono"
              data-testid="staff-password"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Jätä tyhjäksi, niin generoidaan turvallinen</p>
          </div>
        )}
        {isEdit && (
          <div className="col-span-2 flex items-center justify-between rounded-lg border border-zinc-700 p-3">
            <div>
              <Label className="text-sm">Aktiivinen</Label>
              <p className="text-[11px] text-muted-foreground">
                Kun pois päältä, käyttäjä ei voi kirjautua
              </p>
            </div>
            <Switch
              checked={form.is_active}
              onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              data-testid="staff-is-active"
            />
          </div>
        )}
        <div className="col-span-2 space-y-2">
          <Label className="text-xs">Moduulit (mitkä työkalut käyttäjä näkee)</Label>
          <ModuleSelector value={form.modules} onChange={(v) => setForm({ ...form, modules: v })} />
        </div>
        <div className="col-span-2 flex items-center justify-between rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] p-3">
          <div>
            <Label className="text-sm">Saa luoda ulkoisia kokouskutsuja</Label>
            <p className="text-[11px] text-muted-foreground">
              Jos päällä, käyttäjä voi luoda kertakäyttöisiä kutsulinkkejä kokoushuoneeseen (ulkopuolisille).
              Vaatii että kokoushuone-moduuli on aktivoitu.
            </p>
          </div>
          <Switch
            checked={!!form.can_create_external_invites}
            onCheckedChange={(v) => setForm({ ...form, can_create_external_invites: v })}
            data-testid="staff-can-create-external-invites"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Huomiot</Label>
          <Input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="mt-1"
            placeholder="Vapaamuotoinen kuvaus..."
          />
        </div>
      </div>
      <div className="flex gap-2 pt-2 sticky bottom-0 bg-background pb-1">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Peruuta
        </Button>
        <Button
          onClick={save}
          disabled={loading}
          className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-medium"
          data-testid="staff-save"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {isEdit ? "Tallenna muutokset" : "Luo ja aktivoi"}
        </Button>
      </div>
    </div>
  );
}

export default function UsersAdmin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwModal, setPwModal] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 403) navigate("/");
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.email || "").toLowerCase().includes(q) ||
      (u.full_name || "").toLowerCase().includes(q) ||
      (u.company_name || "").toLowerCase().includes(q)
    );
  });

  const resetPw = (u) => {
    setResetTarget({ kind: "user", id: u.id, email: u.email });
  };

  const remove = async (u) => {
    if (!window.confirm(`Poistetaanko käyttäjä ${u.email} pysyvästi?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Käyttäjä poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const stats = {
    total: users.length,
    admins: users.filter((u) => u.role === "admin").length,
    staff: users.filter((u) => u.role === "staff").length,
    customers: users.filter((u) => u.role === "license_customer").length,
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="users-admin">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-amber-100">Käyttäjät</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Hallitse omaa henkilökuntaa ja lisenssiasiakkaiden tunnuksia. Käyttäjät aktivoituvat välittömästi luonnin yhteydessä.
          </p>
        </div>
        <Button
          className="gap-2 bg-amber-500 text-zinc-900 hover:bg-amber-400 font-medium"
          onClick={() => {
            setEditUser(null);
            setShowForm(true);
          }}
          data-testid="users-new"
        >
          <Plus className="w-4 h-4" /> Lisää käyttäjä
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Yhteensä" value={stats.total} />
        <KPI label="Pääkäyttäjät" value={stats.admins} accent="amber" />
        <KPI label="Henkilökunta" value={stats.staff} accent="blue" />
        <KPI label="Lisenssiasiakkaat" value={stats.customers} accent="purple" />
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hae nimellä, sähköpostilla, yrityksellä..."
            className="pl-9"
            data-testid="users-search"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48" data-testid="users-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Kaikki roolit</SelectItem>
            <SelectItem value="admin">Pääkäyttäjät</SelectItem>
            <SelectItem value="staff">Henkilökunta</SelectItem>
            <SelectItem value="license_customer">Lisenssiasiakkaat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Ladataan...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>Ei käyttäjiä hakukriteereillä.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-amber-500/20">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-amber-500/20">
                <th className="py-3 px-3">Nimi</th>
                <th className="py-3 px-3">Sähköposti</th>
                <th className="py-3 px-3">Rooli</th>
                <th className="py-3 px-3">Moduulit</th>
                <th className="py-3 px-3">2FA</th>
                <th className="py-3 px-3">Tila</th>
                <th className="py-3 px-3">Luotu</th>
                <th className="py-3 px-3 w-48"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const rb = ROLE_LABELS[u.role] || ROLE_LABELS.user;
                return (
                  <tr key={u.id} className="border-b border-amber-500/10 hover:bg-amber-500/5 transition" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-3">
                      <p className="font-medium">{u.full_name}</p>
                      {u.company_name && <p className="text-[11px] text-muted-foreground">{u.company_name}</p>}
                    </td>
                    <td className="py-3 px-3 font-mono text-xs">{u.email}</td>
                    <td className="py-3 px-3">
                      <Badge className={`text-[10px] ${rb.cls}`}>{rb.label}</Badge>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-xs text-muted-foreground">{u.modules?.length || 0} moduulia</span>
                    </td>
                    <td className="py-3 px-3">
                      {u.twofa_enabled ? (
                        <Badge className="text-[10px] bg-emerald-500/20 text-emerald-200 border-emerald-500/30">
                          Aktiivinen
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] bg-amber-500/20 text-amber-200 border-amber-500/30">
                          Ei enrollattu
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      {u.is_active ? (
                        <Badge className="text-[10px] bg-emerald-500/20 text-emerald-200 border-emerald-500/30">
                          Aktiivinen
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] bg-red-500/20 text-red-200 border-red-500/30">Suljettu</Badge>
                      )}
                    </td>
                    <td className="py-3 px-3 text-[11px] text-muted-foreground">
                      {(u.created_at || "").split("T")[0]}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="gap-1 text-xs h-8"
                          onClick={() => resetPw(u)}
                          data-testid={`user-reset-${u.id}`}
                          title="Salasana / 2FA -palautus"
                        >
                          <KeyRound className="w-3 h-3" /> Palauta
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-8 h-8 text-amber-300/70 hover:text-amber-200"
                          onClick={() => navigate(`/admin/staff/${u.id}`)}
                          title="Avaa henkilöstökortti"
                          data-testid={`user-profile-${u.id}`}
                        >
                          <IdCard className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-8 h-8"
                          onClick={() => {
                            setEditUser(u);
                            setShowForm(true);
                          }}
                          data-testid={`user-edit-${u.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-8 h-8 text-destructive/60 hover:text-destructive"
                          onClick={() => remove(u)}
                          data-testid={`user-delete-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-amber-400" />
              {editUser ? "Muokkaa käyttäjää" : "Lisää uusi käyttäjä"}
            </DialogTitle>
          </DialogHeader>
          <StaffForm
            user={editUser}
            onSaved={() => {
              setShowForm(false);
              load();
            }}
            onClose={() => setShowForm(false)}
            onShowPassword={(d) => setPwModal({ ...d, title: "Tunnukset luotu – välitä käyttäjälle" })}
          />
        </DialogContent>
      </Dialog>

      <PasswordModal
        open={!!pwModal}
        onClose={() => setPwModal(null)}
        email={pwModal?.email}
        password={pwModal?.password}
        title={pwModal?.title}
      />

      <ResetActionsModal
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        target={resetTarget}
      />
    </div>
  );
}

function KPI({ label, value, accent = "amber" }) {
  const colour = {
    amber: "text-amber-300",
    blue: "text-blue-300",
    purple: "text-purple-300",
    emerald: "text-emerald-300",
  }[accent] || "text-foreground";
  return (
    <Card className="bg-zinc-900/60 border-amber-500/20">
      <CardContent className="p-4">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${colour}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
